import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, SimpleChanges, AfterViewInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrganisationService } from 'src/services/organisation.service';
import { OrganisationModel } from 'src/models/pilotage/organisationModel';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatSelectModule } from '@angular/material/select';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';

@Component({
    selector: 'app-organisations-managment',
    imports: [
        CommonModule,
        MatProgressSpinnerModule,
        TranslateModule,
        MatIconModule,
        FormsModule,
        NgxMatSelectSearchModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule
    ],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ],
    templateUrl: './organisations-managment.component.html',
    styleUrl: './organisations-managment.component.scss'
})
export class OrganisationsManagmentComponent implements OnInit, AfterViewInit, OnChanges {
  entites: OrganisationModel[] = [];
  groupes: OrganisationModel[] = [];
  selectedGroupIds: string[] = [];
  filteredEntites: OrganisationModel[] = [];
  filteredEnCours: OrganisationModel[] = [];
  isLoading: boolean = false;
  searchTerm: string = '';
  isFiltering: boolean = false;

  entitesAffiches: OrganisationModel[] = [];
  entitesFiltreEnCours: OrganisationModel[] = [];

  pageIndex: number = 0;
  pageSize: number = 10;
  currentPage: number = 0;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  selectedGroupId: string = "";

  displayedColumns: string[] = [
    'nom',
    'entiteId',
    'neq',
    'idUnique',
    'group'
  ];

  dataSource!: MatTableDataSource<OrganisationModel>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  protected _onDestroy = new Subject<void>();
  groupFilterCtrl: FormControl = new FormControl();
  filteredGroupes: ReplaySubject<OrganisationModel[]> = new ReplaySubject<OrganisationModel[]>(1);

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private organisationsService: OrganisationService,
    public translate: TranslateService
  ) {
    translate.setDefaultLang('fr');
    this.dataSource = new MatTableDataSource<OrganisationModel>([]);
  }

  ngOnInit(): void {
    this.loadOrganisations();

    this.groupFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterGroupes();
      });
  }

  ngAfterViewInit() {
    if (this.sort) {
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.updatePaginatorLength();
    }
  }


  private filterGroupes() {
    if (!this.groupes) {
      return;
    }
    let search = this.groupFilterCtrl.value;
    if (!search) {
      this.filteredGroupes.next(this.groupes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredGroupes.next(
      this.groupes.filter(groupe => groupe.nom.toLowerCase().indexOf(search) > -1)
    );
  }

  filtre(): void {
    if (this.isFiltering) return;
    this.isFiltering = true;

    try {
      this.applyFilters();

    } finally {
      this.isFiltering = false;
    }
  }



  applyFilters(): void {
    let filtered = [...this.entites];

    if (this.selectedGroupId) {
      filtered = filtered.filter(org => org.groupeId !== null && org.groupeId === Number(this.selectedGroupId));
    }
    this.entitesFiltreEnCours = filtered;

    this.filtreRecherche();
    this.totalItems = this.entitesFiltreEnCours.length;
    this.paginer();
  }

  filtreRecherche(): void {
    if (this.searchTerm && this.searchTerm.length > 2) {
      const textRechercher = this.searchTerm.trim().toLowerCase();
      this.entitesFiltreEnCours = this.entitesFiltreEnCours.filter(entite => {
        const checkValue = (value: any): boolean => {
          if (value && typeof value === 'object') {
            return Object.values(value).some(v => checkValue(v));
          }
          return value && String(value).toLowerCase().includes(textRechercher);
        };

        return Object.values(entite).some(value => checkValue(value));
      });
    }
  }


  paginer(event?: PageEvent) {
    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    }
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.entitesAffiches = this.entitesFiltreEnCours.slice(startIndex, endIndex);
    this.dataSource.data = this.entitesAffiches;
    this.changeDetectorRef.detectChanges();
  }

  private loadOrganisations(): void {
    this.isLoading = true;
    this.organisationsService.obtenirOrganisations().subscribe(
      (res) => {
        this.entites = res.entites;
        this.groupes = res.groupes;
        this.filteredGroupes.next(this.groupes.slice());
        this.entitesFiltreEnCours = this.entites;
        this.dataSource.data = this.entitesFiltreEnCours;
        this.totalItems = this.entites.length;
        this.paginer();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading organizations:', error);
        this.isLoading = false;
      }
    );
  }
  private updatePaginatorLength(): void {
    if (this.paginator) {
      this.paginator.length = this.entitesFiltreEnCours.length;
      this.totalItems = this.entitesFiltreEnCours.length;
      this.paginator.page.emit();
      this.changeDetectorRef.detectChanges();
    }
  }
  getGroupName(groupeId: number | null | undefined): string {
    if (!groupeId) return '';
    const groupe = this.groupes.find(g => g.entiteId === groupeId);
    return groupe?.nom || '';
  }

  reinitialiserFiltre(): void {
    this.selectedGroupId = "";
    this.searchTerm = '';
    this.entitesFiltreEnCours = this.entites;
    this.filtreRecherche();
    this.totalItems = this.entitesFiltreEnCours.length;
    this.paginer();
  }
}
